<template>
  <div class="hero">
    <img :src="heroImage" :alt="title" class="hero-bg" />
    <div class="hero-card">
      <span class="hero-eyebrow">{{ $t('hero.location') }}</span>
      <h1 class="hero-title">{{ title }}</h1>
      <div class="hero-underline"></div>
      <p v-if="subtitle" class="hero-subtitle">{{ subtitle }}</p>
      <router-link to="/galery" class="hero-cta">
        <span>{{ $t('hero.cta') }}</span>
        <span class="hero-cta-arrow">→</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'HeaderComponent',
  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();

    const heroImage = computed(() => route.meta.heroImage || '/background/A9bac.jpg');
    const subtitle = computed(() => route.meta.heroSubtitleKey ? t(route.meta.heroSubtitleKey) : '');
    const title = computed(() => route.meta.heroTitleKey ? t(route.meta.heroTitleKey) : t('name'));

    return {
      t,
      locale,
      title,
      subtitle,
      heroImage
    };
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  height: 660px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

/* Frosted glass card over the photo */
.hero-card {
  position: relative;
  z-index: 2;
  max-width: 620px;
  margin: 0 32px;
  padding: 56px 64px;
  text-align: center;
  background: rgba(14, 59, 67, 0.52);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius-lg);
  box-shadow: 0 30px 80px rgba(6, 18, 20, 0.35);
}

.hero-eyebrow {
  display: inline-block;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--color-accent-light);
  opacity: 0;
  animation: heroFadeUp 0.7s var(--ease) 0.1s forwards;
}

.hero-title {
  font-family: var(--font-display);
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 600;
  line-height: 1.15;
  color: #ffffff;
  margin: 16px 0 0;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.25);
  opacity: 0;
  animation: heroFadeUp 0.7s var(--ease) 0.25s forwards;
}

.hero-underline {
  width: 70px;
  height: 3px;
  background: var(--color-accent);
  margin: 24px auto;
  opacity: 0;
  animation: heroFadeUp 0.7s var(--ease) 0.4s forwards;
}

.hero-subtitle {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 32px;
  opacity: 0;
  animation: heroFadeUp 0.7s var(--ease) 0.5s forwards;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--color-accent);
  color: #ffffff !important;
  text-decoration: none !important;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 15px 30px;
  border-radius: 30px;
  transition: all 0.35s var(--ease);
  opacity: 0;
  animation: heroFadeUp 0.7s var(--ease) 0.65s forwards;
}

.hero-cta:hover {
  background: var(--color-accent-light);
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(200, 121, 63, 0.4);
}

.hero-cta-arrow {
  transition: transform 0.3s ease;
}

.hero-cta:hover .hero-cta-arrow {
  transform: translateX(4px);
}

@keyframes heroFadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Tablet */
@media (max-width: 1024px) {
  .hero { height: 580px; }
  .hero-card { padding: 48px 44px; }
}

/* Mobile */
@media (max-width: 768px) {
  .hero { height: 520px; }
  .hero-card {
    padding: 36px 26px;
    margin: 0 20px;
  }
  .hero-title { font-size: 28px; }
  .hero-subtitle { font-size: 15px; }
}

@media (max-width: 420px) {
  .hero { height: 480px; }
  .hero-card { padding: 30px 20px; }
}

@media (prefers-reduced-motion: reduce) {
  .hero-eyebrow, .hero-title, .hero-underline, .hero-subtitle, .hero-cta {
    animation: none;
    opacity: 1;
  }
}
</style>
